import type { MultiLangContent } from '@carlito/bookito-client';
import { MultilangFormEditData, TranslationsStatus, isFieldMissing } from '@carlito/intl';
import classNames from 'classnames';
import { Field, FieldAttributes, useFormikContext } from 'formik';
import { ComponentProps, forwardRef } from 'react';
import LabelledFormElement, { LabelledFormElementProps } from '../LabelledFormElement';

type PossibleInputValue = MultiLangContent | ComponentProps<'input'>['value'];

function isMultiLangContent(value: any): value is MultiLangContent {
  return typeof value === 'object' && value.type === 'multi_lang';
}

export interface LabelledMultiLangInputProps
  extends FieldAttributes<any>,
    LabelledFormElementProps {
  enforceMultilang?: boolean;
}

export const MultiLangInput = forwardRef<
  HTMLInputElement | HTMLTextAreaElement,
  LabelledMultiLangInputProps
>(
  (
    {
      label,
      className,
      loading,
      labelClassName,
      disableToggle,
      name = '',
      isAutoCompleteRequired = true,
      disabled,
      enforceMultilang,
      ...inputProps
    },
    ref
  ) => {
    const { values, getFieldProps, setFieldValue, setFieldTouched, status } =
      useFormikContext<MultilangFormEditData<any>>();

    const formStatus = status as { translations: TranslationsStatus } | undefined;

    const fieldMissing =
      !!formStatus &&
      isFieldMissing({
        fieldName: name,
        language: values.activeLanguage,
        status: formStatus.translations,
      });

    const fieldProps = getFieldProps<PossibleInputValue>(name);
    const { onChange } = fieldProps;
    let { value } = fieldProps;

    let valueToUse = value;

    if (isMultiLangContent(value) || enforceMultilang) {
      const { activeLanguage } = values;

      // cast to MultiLangContent
      if (!isMultiLangContent(value)) {
        value = {
          type: 'multi_lang',
          values: {
            [activeLanguage]: value,
          },
        };
      }

      valueToUse = value.values[activeLanguage];
    }

    return (
      <LabelledFormElement
        labelClassName={labelClassName}
        className={className}
        disableToggle={disableToggle}
        loading={loading}
        label={label}
        badge={fieldMissing ? { color: 'yellow' } : undefined}
      >
        <Field
          as="input"
          {...inputProps}
          disabled={disabled}
          onChange={(e: React.ChangeEvent<any>) => {
            if (isMultiLangContent(value)) {
              setFieldValue(name, {
                ...value,
                values: {
                  ...value.values,
                  [values.activeLanguage]: e.target.value,
                },
              });
              setFieldTouched(name, true);
              return;
            }
            onChange(e);
            inputProps.onChange?.(e);
          }}
          value={valueToUse}
          name={name}
          ref={ref}
          autocomplete={isAutoCompleteRequired ? 'on' : 'off'}
          className={classNames(
            'flex-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full min-w-0 rounded-md sm:text-sm border-gray-300',
            {
              // disabled
              'bg-gray-100 cursor-not-allowed': disabled,
            }
          )}
        />
      </LabelledFormElement>
    );
  }
);
export default MultiLangInput;
